/**
 * LitePlanSteps — the tier-2 lite funnel's stand-in for the planner. Tier-2 free users get no
 * AI plan (cost), so where a tier-1 user waits for their plan card this shows five generic
 * steps ticking off (~10 s), then hands on to the same fast "generating" loader tier 1 gets.
 * `completed`: the finished list, kept in the chat above the loader and the preview (as a
 * tier-1 plan card stays above its video).
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Loader2 } from 'lucide-react';
import { cn } from '@/v2/utils';
import { BatonLoader } from '@/v2/components/BatonLoader';

const STEP_KEYS = ['liteStepIdea', 'liteStepAudience', 'liteStepHook', 'liteStepStory', 'liteStepLook'] as const;
const STEP_MS = 2000;

export function LitePlanSteps({ onDone, completed }: { onDone?: () => void; completed?: boolean }) {
  const { t } = useTranslation('v2');
  const [ticked, setDone] = useState(0);
  const done = completed ? STEP_KEYS.length : ticked;
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    if (completed) return;
    if (done >= STEP_KEYS.length) {
      const t = setTimeout(() => onDoneRef.current?.(), 600); // a beat on the finished list before the loader
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setDone((n) => n + 1), STEP_MS);
    return () => clearTimeout(t);
  }, [done, completed]);

  const active = Math.min(done, STEP_KEYS.length - 1);
  return (
    <div className="space-y-3 animate-fade-in">
      {!completed && (
        <div className="flex items-center gap-3.5 py-1">
          <BatonLoader className="shrink-0" />
          <span key={active} className="text-base font-bold bg-gradient-to-r from-primary via-accent to-primary bg-[length:200%_auto] bg-clip-text text-transparent animate-shimmer animate-fade-in">
            {t(`previewFunnel.${STEP_KEYS[active]}`)}
          </span>
        </div>
      )}
      <ul className={cn('rounded-2xl bg-surface-light/60 border border-glass-border p-4 sm:p-5 space-y-2.5',
        !completed && done >= STEP_KEYS.length && 'animate-pulse-glow')}>
        {STEP_KEYS.map((k, i) => {
          const isDone = i < done;
          const isActive = i === done;
          return (
            <li key={k} className={cn('flex items-center gap-3 text-sm sm:text-base font-medium transition-colors duration-300',
              isDone ? 'text-text-primary' : isActive ? 'text-text-secondary' : 'text-text-tertiary/60')}>
              {isDone ? (
                <span className="flex w-5 h-5 shrink-0 items-center justify-center rounded-full bg-green-500/90 animate-pop">
                  <Check className="w-3 h-3 text-white" strokeWidth={3} />
                </span>
              ) : isActive ? (
                <Loader2 className="w-5 h-5 shrink-0 text-primary animate-spin" />
              ) : (
                <span className="w-5 h-5 shrink-0 rounded-full border border-glass-border" />
              )}
              <span className={cn(isDone && 'animate-check-row')}>{t(`previewFunnel.${k}`)}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
